/* Admin → Master Data. Manage the master field registry (RSM, TRIM, Territory,
 * Distributor, Retailer + any admin-added masters). Each master field has a
 * key (stable identifier), label (shown in UI) and a list of values that
 * populate scope dropdowns in Users & Access. Admin can add new master fields.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Database, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["admin", "spykar_admin", "system_admin"]);

export default function AdminMasters() {
    const { user } = useAuth();
    const [masters, setMasters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [valuesText, setValuesText] = useState("");
    const [showNewModal, setShowNewModal] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await http.get("/admin/masters");
            setMasters(r.data);
            if (r.data.length && !selectedKey) setSelectedKey(r.data[0].key);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (user && ADMIN_ROLES.has(user.role)) load(); }, [user]);

    // Whenever selected master changes, refresh valuesText
    useEffect(() => {
        const m = masters.find((x) => x.key === selectedKey);
        if (m) setValuesText((m.values || []).join("\n"));
    }, [selectedKey, masters]);

    if (user && !ADMIN_ROLES.has(user.role)) {
        return <Navigate to="/" replace />;
    }

    const saveValues = async () => {
        if (!selectedKey) return;
        const values = valuesText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
        try {
            await http.put(`/admin/masters/${selectedKey}/values`, { values });
            toast.success(`${selectedKey} updated (${values.length} values)`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const deleteMaster = async (key) => {
        if (!window.confirm(`Delete master field "${key}"?`)) return;
        try {
            await http.delete(`/admin/masters/${key}`);
            toast.success("Master field deleted");
            setSelectedKey(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const selected = masters.find((x) => x.key === selectedKey);

    return (
        <div className="space-y-4" data-testid="admin-masters-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="flex items-center gap-2 text-[20px] font-semibold">
                        <Database className="h-5 w-5" /> Master Data
                    </h1>
                    <p className="text-[12px] text-muted-foreground">
                        Manage the master field registry. These values populate scope dropdowns in Users &amp; Access.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="admin-masters-refresh"
                        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] hover:bg-muted/40"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                    <button
                        onClick={() => setShowNewModal(true)}
                        data-testid="admin-masters-new"
                        className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add Master Field
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_1fr]">
                {/* Left: master field list */}
                <div className="overflow-hidden rounded-sm border border-border bg-card">
                    <div className="border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Fields ({masters.length})
                    </div>
                    <div className="max-h-[520px] overflow-auto">
                        {masters.map((m) => (
                            <button
                                key={m.key}
                                onClick={() => setSelectedKey(m.key)}
                                data-testid={`admin-master-${m.key}`}
                                className={`flex w-full items-center justify-between border-b border-border px-3 py-2 text-left text-[12px] hover:bg-muted/30 ${
                                    selectedKey === m.key ? "bg-primary/10 font-semibold" : ""
                                }`}
                            >
                                <span>{m.label}</span>
                                <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                    {(m.values || []).length}
                                </span>
                            </button>
                        ))}
                        {!loading && masters.length === 0 && (
                            <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                                No master fields.
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: value editor */}
                <div className="overflow-hidden rounded-sm border border-border bg-card">
                    {selected ? (
                        <>
                            <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
                                <div>
                                    <div className="text-[13px] font-semibold">{selected.label}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                        key: {selected.key} {selected.is_system && "· system"}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {!selected.is_system && (
                                        <button
                                            onClick={() => deleteMaster(selected.key)}
                                            data-testid={`admin-master-delete-${selected.key}`}
                                            className="inline-flex items-center gap-1 rounded-sm border border-red-500/40 px-2 py-1 text-[11px] text-red-500 hover:bg-red-500/10"
                                        >
                                            <Trash2 className="h-3 w-3" /> Delete
                                        </button>
                                    )}
                                    <button
                                        onClick={saveValues}
                                        data-testid={`admin-master-save-${selected.key}`}
                                        className="inline-flex items-center gap-1 rounded-sm bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground hover:opacity-90"
                                    >
                                        <Save className="h-3 w-3" /> Save values
                                    </button>
                                </div>
                            </div>
                            <div className="p-3">
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    Values · one per line
                                </div>
                                <textarea
                                    data-testid={`admin-master-textarea-${selected.key}`}
                                    value={valuesText}
                                    onChange={(e) => setValuesText(e.target.value)}
                                    rows={20}
                                    className="w-full rounded-sm border border-border bg-background p-2 font-mono text-[11px] leading-relaxed focus:border-primary/60 focus:outline-none"
                                />
                                <div className="mt-1 text-[10px] text-muted-foreground">
                                    {valuesText.split(/\r?\n/).filter((s) => s.trim()).length} lines · duplicates
                                    are removed on save
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="p-8 text-center text-[12px] text-muted-foreground">
                            Select a master field on the left, or click <em>Add Master Field</em>.
                        </div>
                    )}
                </div>
            </div>

            {showNewModal && (
                <NewMasterModal
                    onClose={() => setShowNewModal(false)}
                    onCreated={(k) => { setShowNewModal(false); setSelectedKey(k); load(); }}
                />
            )}
        </div>
    );
}

function NewMasterModal({ onClose, onCreated }) {
    const [key, setKey] = useState("");
    const [label, setLabel] = useState("");
    const [valuesText, setValuesText] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!key.trim() || !label.trim()) return toast.error("Key and label are required");
        setSaving(true);
        try {
            const values = valuesText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
            await http.post("/admin/masters", { key: key.trim().toLowerCase(), label: label.trim(), values });
            toast.success(`Master field ${label} created`);
            onCreated(key.trim().toLowerCase());
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg overflow-hidden rounded-md border border-border bg-card shadow-lg" data-testid="admin-master-new-modal">
                <div className="border-b border-border px-5 py-3 text-[14px] font-semibold">Add Master Field</div>
                <div className="space-y-3 p-5">
                    <label className="block">
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Key · alphanumeric/underscore (e.g. brand, cluster)
                        </div>
                        <input
                            type="text"
                            data-testid="admin-master-new-key"
                            value={key}
                            onChange={(e) => setKey(e.target.value.toLowerCase())}
                            className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                        />
                    </label>
                    <label className="block">
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Label · shown in UI
                        </div>
                        <input
                            type="text"
                            data-testid="admin-master-new-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                        />
                    </label>
                    <label className="block">
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Values · one per line (optional)
                        </div>
                        <textarea
                            data-testid="admin-master-new-values"
                            value={valuesText}
                            onChange={(e) => setValuesText(e.target.value)}
                            rows={8}
                            className="w-full rounded-sm border border-border bg-background p-2 font-mono text-[11px]"
                        />
                    </label>
                </div>
                <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
                    <button
                        onClick={onClose}
                        data-testid="admin-master-new-cancel"
                        className="rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-muted/40"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="admin-master-new-save"
                        className="rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}
